import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Collapse,
  IconButton,

} from '@mui/material'
import {
  TableChart as ExcelIcon,
  Assessment as ReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import ReportsTabs from '../../components/reports/ReportsTabs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import { usePnlReport, useGeneratePnlExcel } from '../../hooks/api/useReports'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import { PnlExpenseBreakdown } from '../../types'

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader = ({ label }: { label: string }) => (
  <Typography
    variant="body2"
    fontWeight="bold"
    sx={{ mt: 2.5, mb: 0.5, color: 'text.primary', letterSpacing: 0.3 }}
  >
    {label}
  </Typography>
)

const LineRow = ({
  label,
  value,
  indent = false,
  color,
}: {
  label: string
  value: string | number
  indent?: boolean
  color?: string
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      pl: indent ? 2.5 : 0,
      py: 0.4,
    }}
  >
    <Typography variant="body2" color={color ?? 'text.secondary'}>
      {label}
    </Typography>
    <Typography variant="body2" color={color ?? 'text.secondary'} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
      {typeof value === 'number' ? formatCurrency(value) : value}
    </Typography>
  </Box>
)

const TotalRow = ({
  label,
  amount,
  color,
  size = 'normal',
}: {
  label: string
  amount: number
  color?: string
  size?: 'normal' | 'large'
}) => (
  <>
    <Divider sx={{ mt: 1, mb: 0.5 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography
        variant={size === 'large' ? 'subtitle1' : 'body2'}
        fontWeight="bold"
        color={color ?? 'text.primary'}
      >
        {label}
      </Typography>
      <Typography
        variant={size === 'large' ? 'subtitle1' : 'body2'}
        fontWeight="bold"
        color={color ?? 'text.primary'}
        sx={{ ml: 2, whiteSpace: 'nowrap' }}
      >
        {formatCurrency(amount)}
      </Typography>
    </Box>
  </>
)

// ─── Main Page ────────────────────────────────────────────────────────────────

const PnlReportPage = () => {

  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [viewReport, setViewReport] = useState(false)
  const [expensesExpanded, setExpensesExpanded] = useState(true)

  const fmt = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : '')

  const { data, isLoading } = usePnlReport(
    viewReport && startDate && endDate
      ? { startDate: fmt(startDate), endDate: fmt(endDate), format: 'json' }
      : undefined
  )

  const excelMutation = useGeneratePnlExcel()

  const handleViewReport = () => {
    if (startDate && endDate) setViewReport(true)
  }

  const handleExport = () => {
    if (startDate && endDate) {
      excelMutation.mutate({ startDate: fmt(startDate), endDate: fmt(endDate) })
    }
  }

  const netProfitColor =
    data?.summary.net_profit !== undefined
      ? data.summary.net_profit >= 0
        ? 'success.main'
        : 'error.main'
      : 'text.primary'

  const grossProfitColor =
    data?.summary.gross_profit !== undefined
      ? data.summary.gross_profit >= 0
        ? 'success.main'
        : 'error.main'
      : 'text.primary'

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ReportIcon /> Reports
        </Typography>

        <ReportsTabs activeTab={2} />

        {/* Filter bar */}
        <Paper sx={{ p: 2.5, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(v) => {
              setStartDate(v)
              setViewReport(false)
            }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            minDate={startDate ?? undefined}
            onChange={(v) => {
              setEndDate(v)
              setViewReport(false)
            }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            onClick={handleViewReport}
            disabled={!startDate || !endDate || isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : <ReportIcon />}
          >
            View Report
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExport}
            disabled={!startDate || !endDate || excelMutation.isPending}
            startIcon={
              excelMutation.isPending ? <CircularProgress size={18} /> : <ExcelIcon />
            }
          >
            Export Excel
          </Button>
        </Paper>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Generating report…
            </Typography>
          </Box>
        )}

        {/* P&L Statement */}
        {viewReport && data && !isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ p: { xs: 3, sm: 5 }, width: '100%', maxWidth: 700 }}>

              {/* Company header */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {data.company.name}
                </Typography>
                <Typography variant="h6" fontWeight="medium">
                  Profit &amp; Loss
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {format(new Date(startDate!), 'MMM d, yyyy')}
                  {' – '}
                  {format(new Date(endDate!), 'MMM d, yyyy')}
                </Typography>
              </Box>

              <Divider sx={{ mb: 1 }} />

              {/* ── Income ── */}
              <SectionHeader label="Income" />
              <LineRow
                label={`Revenue (${data.summary.bill_count} bills)`}
                value={data.summary.revenue}
                indent
              />
              <TotalRow label="Total Income" amount={data.summary.revenue} />

              {/* ── COGS ── */}
              <SectionHeader label="Cost of Goods Sold" />
              <LineRow
                label={`Purchase Invoices (${data.summary.invoice_count})`}
                value={data.summary.cogs}
                indent
              />
              <TotalRow label="Total COGS" amount={data.summary.cogs} />

              {/* ── Gross Profit ── */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 2,
                  py: 0.75,
                  borderTop: 2,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={grossProfitColor}>
                  Gross Profit
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={grossProfitColor}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatCurrency(data.summary.gross_profit)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Gross Margin
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.summary.gross_margin.toFixed(1)}%
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* ── Operating Expenses ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ letterSpacing: 0.3 }}>
                  Operating Expenses
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setExpensesExpanded((p) => !p)}
                  sx={{ ml: 1 }}
                >
                  {expensesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>

              <Collapse in={expensesExpanded}>
                {data.expense_breakdown.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ pl: 2.5, py: 0.5 }}
                  >
                    No expenses in this period
                  </Typography>
                ) : (
                  data.expense_breakdown.map((e: PnlExpenseBreakdown) => (
                    <LineRow
                      key={e.category}
                      label={`${e.category} (${e.count})`}
                      value={e.amount}
                      indent
                    />
                  ))
                )}
              </Collapse>

              <TotalRow label="Total Operating Expenses" amount={data.summary.operating_expenses} />

              {/* ── Net Profit ── */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 2,
                  py: 1,
                  borderTop: 3,
                  borderBottom: 3,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" fontWeight="bold" color={netProfitColor}>
                  Net Profit
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={netProfitColor}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatCurrency(data.summary.net_profit)}
                </Typography>
              </Box>

            </Paper>
          </Box>
        )}

        {/* Zero state */}
        {viewReport && !isLoading && !data && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography color="text.secondary">
              No data found for the selected date range.
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default PnlReportPage
